"use client";

import { useState, useEffect, useMemo, useCallback } from "react";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  loadCatalog,
  type CurriculumCatalog,
  type CurriculumTopicMeta,
} from "@/lib/curriculum";
import {
  ChevronRight, ChevronDown, Search, BookOpen, Loader2,
  UtensilsCrossed, Car, ShoppingBag, Heart, Briefcase, Home,
  Users, Plane, Wrench, GraduationCap, MessageCircle,
} from "lucide-react";
import { useLanguage } from "@/lib/language-context";
import { getCategoryInfo, getCategoryName, type CategoryId } from "@/data/dialogue-categories";

const CATEGORY_ICONS: Record<CategoryId, React.ComponentType<{ className?: string }>> = {
  food: UtensilsCrossed,
  transport: Car,
  shopping: ShoppingBag,
  health: Heart,
  work: Briefcase,
  home: Home,
  social: Users,
  travel: Plane,
  services: Wrench,
  education: GraduationCap,
  other: MessageCircle,
};

const DIFFICULTY_STYLES: Record<string, { badge: string; dot: string }> = {
  beginner: {
    badge: "bg-success/15 text-success border-success/30",
    dot: "bg-success",
  },
  intermediate: {
    badge: "bg-primary/15 text-primary border-primary/30",
    dot: "bg-primary",
  },
  advanced: {
    badge: "bg-accent/15 text-accent border-accent/30",
    dot: "bg-accent",
  },
};

interface CurriculumBrowserProps {
  onAdaptTopic: (topic: CurriculumTopicMeta) => void;
}

export function CurriculumBrowser({ onAdaptTopic }: CurriculumBrowserProps) {
  const { targetLanguage, nativeLanguage } = useLanguage();
  const [catalog, setCatalog] = useState<CurriculumCatalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedPhases, setExpandedPhases] = useState<Set<string>>(new Set());

  // Load catalog when language changes
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setCatalog(null);

    loadCatalog(targetLanguage.code, nativeLanguage.code).then((data) => {
      if (!cancelled) {
        setCatalog(data);
        setLoading(false);
        // Auto-expand first phase if catalog loaded
        if (data?.phases[0]) {
          setExpandedPhases(new Set([data.phases[0].id]));
        }
      }
    });

    return () => { cancelled = true; };
  }, [targetLanguage.code, nativeLanguage.code]);

  const togglePhase = useCallback((phaseId: string) => {
    setExpandedPhases((prev) => {
      const next = new Set(prev);
      if (next.has(phaseId)) {
        next.delete(phaseId);
      } else {
        next.add(phaseId);
        setTimeout(() => {
          document.getElementById(`curriculum-phase-${phaseId}`)
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 50);
      }
      return next;
    });
  }, []);

  const handleSelectTopic = useCallback(
    (topicMeta: CurriculumTopicMeta) => {
      // Always go through the adaptation/streaming path so the user sees
      // the full progress → celebration → dialogue flow (with TTS warm-up,
      // vocabulary extraction, vocalization, etc.).
      if (onAdaptTopic) {
        onAdaptTopic(topicMeta);
      }
    },
    [onAdaptTopic],
  );

  // Filter topics by search query
  const filteredPhases = useMemo(() => {
    if (!catalog) return [];
    if (!searchQuery.trim()) return catalog.phases;

    const q = searchQuery.toLowerCase();
    return catalog.phases
      .map((phase) => ({
        ...phase,
        topics: phase.topics.filter(
          (t) =>
            t.title.toLowerCase().includes(q) ||
            t.titleNative.toLowerCase().includes(q) ||
            t.description.toLowerCase().includes(q) ||
            t.descriptionNative.toLowerCase().includes(q),
        ),
      }))
      .filter((phase) => phase.topics.length > 0);
  }, [catalog, searchQuery]);

  const totalTopics = catalog
    ? catalog.phases.reduce((sum, p) => sum + p.topics.length, 0)
    : 0;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center animate-fade-up">
        <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
        <p className="text-sm text-muted-foreground">Loading curriculum...</p>
      </div>
    );
  }

  if (!catalog) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center animate-fade-up">
        <BookOpen className="w-12 h-12 text-muted-foreground/50 mb-4" />
        <p className="text-muted-foreground text-sm max-w-xs">
          No curriculum available for {targetLanguage.name} yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={`Search ${totalTopics} topics...`}
          className="w-full pl-9 pr-4 py-2.5 text-sm rounded-xl border border-border/60 bg-card/80 placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40"
        />
      </div>

      {/* Phase collapsibles */}
      <div className="space-y-3">
        {filteredPhases.map((phase) => {
          const isExpanded = expandedPhases.has(phase.id) || searchQuery.trim() !== "";

          return (
            <div
              key={phase.id}
              id={`curriculum-phase-${phase.id}`}
              className="rounded-xl overflow-hidden scroll-mt-4"
            >
              {/* Phase header */}
              <button
                type="button"
                onClick={() => togglePhase(phase.id)}
                className={`w-full flex items-center gap-3 p-4 rounded-xl border transition-all duration-200 text-left ${
                  isExpanded
                    ? "bg-primary/5 border-primary/30 shadow-sm"
                    : "bg-card/80 border-border/60 hover:border-primary/30 hover:bg-card"
                }`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  isExpanded ? "bg-primary/15" : "bg-muted/50"
                }`}>
                  <BookOpen className={`w-5 h-5 ${isExpanded ? "text-primary" : "text-muted-foreground"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{phase.title}</div>
                  <div className="text-xs text-muted-foreground">
                    {phase.topics.length} {phase.topics.length === 1 ? "topic" : "topics"}
                  </div>
                </div>
                <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${
                  isExpanded ? "rotate-180" : ""
                }`} />
              </button>

              {/* Topics */}
              {isExpanded && (
                <div className="pt-3 space-y-3 animate-fade-up">
                  {phase.topics.map((topic, index) => (
                    <CurriculumTopicCard
                      key={topic.id}
                      topic={topic}
                      index={index}
                      onSelect={() => handleSelectTopic(topic)}
                      nativeLocale={nativeLanguage.code}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredPhases.length === 0 && searchQuery && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Search className="w-8 h-8 text-muted-foreground/50 mb-3" />
          <p className="text-sm text-muted-foreground">
            No topics match &ldquo;{searchQuery}&rdquo;
          </p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Topic Card
// ---------------------------------------------------------------------------

function CurriculumTopicCard({
  topic,
  index,
  onSelect,
  nativeLocale,
}: {
  topic: CurriculumTopicMeta;
  index: number;
  onSelect: () => void;
  nativeLocale: string;
}) {
  const catInfo = getCategoryInfo(topic.category);
  const Icon = CATEGORY_ICONS[topic.category] || MessageCircle;
  const categoryName = getCategoryName(topic.category, nativeLocale as any);
  const styles = DIFFICULTY_STYLES[topic.difficulty] ?? DIFFICULTY_STYLES.intermediate!;

  return (
    <Card
      className="card-interactive cursor-pointer border-border/60 bg-card/80 backdrop-blur-sm hover:border-primary/40 animate-fade-up overflow-hidden group relative"
      style={{ animationDelay: `${index * 40}ms` }}
      onClick={onSelect}
    >
      {/* Decorative accent line */}
      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-primary via-primary/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      <CardHeader className="pb-3 pl-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            {/* Category + difficulty badges */}
            <div className="flex items-center gap-2 mb-1.5">
              <span className={`flex items-center gap-1 text-xs ${catInfo.color}`}>
                <Icon className="w-3 h-3" />
                <span>{categoryName}</span>
              </span>
              <Badge
                variant="outline"
                className={`text-[10px] py-0 px-1.5 font-normal ${styles.badge}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${styles.dot} mr-1`} />
                {topic.difficulty}
              </Badge>
            </div>

            <CardTitle className="text-base font-medium text-card-foreground leading-snug mb-1 line-clamp-2">
              {topic.titleNative || topic.title}
            </CardTitle>
            <CardDescription className="text-sm line-clamp-2 leading-relaxed">
              {topic.descriptionNative || topic.description}
            </CardDescription>
          </div>

          <div className="w-8 h-8 rounded-full bg-card/80 backdrop-blur-sm flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-all duration-300 shadow-sm">
            <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all duration-300" />
          </div>
        </div>
      </CardHeader>

      {/* Footer with metadata */}
      <div className="px-5 pb-3 flex items-center gap-3 text-xs text-muted-foreground">
        {topic.speaker && (
          <span className="truncate">{topic.speaker}</span>
        )}
        {topic.wordCount > 0 && (
          <span className="flex items-center gap-1 ml-auto">
            <BookOpen className="w-3 h-3" />
            {topic.wordCount} words
          </span>
        )}
      </div>
    </Card>
  );
}
